import { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBath, faBriefcase, faBuilding, faCheckCircle, faChevronRight, faCouch, faHammer, faHome, faLandmark, faPause, faPlay, faTrowelBricks, faVolumeMute, faVolumeUp, faTimes } from '@fortawesome/free-solid-svg-icons';

// Import images
import img1 from '../assets/img1.png';
import img2 from '../assets/img2.png';
import img3 from '../assets/img3.jpeg';
import img4 from '../assets/img4.jpeg';
import img5 from '../assets/img5.jpeg';
import img6 from '../assets/img6.jpeg';
import img7 from '../assets/img7.jpeg';

import video1 from '../assets/v1.mp4';
import video2 from '../assets/v2.mp4';
import { Link } from 'react-router-dom';

const Services = () => {
  const [isVideo1Playing, setIsVideo1Playing] = useState(false);
  const [isVideo2Playing, setIsVideo2Playing] = useState(false);
  const [isVideo1Muted, setIsVideo1Muted] = useState(true);
  const [isVideo2Muted, setIsVideo2Muted] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);

  const stats = [
    { icon: faBuilding, value: "Reliable", label: "Project Planning & Execution" },
    { icon: faHammer, value: "Efficient", label: "Work Process & Techniques" },
    { icon: faHome, value: "Secure", label: "Material & Site Safety" },
    { icon: faBriefcase, value: "Responsive", label: "Client Communication" },
  ];

  const services = [
    {
      title: "New Builds",
      icon: faBuilding,
      description: "Complete construction of new residential and commercial properties from ground up."
    },
    {
      title: "Large and Small Extensions",
      icon: faHome,
      description: "Seamless property expansions, from small additions to major structural extensions."
    },
    {
      title: "Renovations/Refurbishment",
      icon: faHammer,
      description: "Transform existing spaces with comprehensive renovation and refurbishment services."
    },
    {
      title: "Kitchens/Bathrooms",
      icon: faBath,
      description: "Custom kitchen and bathroom installations, remodeling, and complete fit-outs."
    },
    {
      title: "Interiors",
      icon: faCouch,
      description: "Complete interior design, finishing, and decoration services for all property types."
    },
    {
      title: "Stonework/Brickwork",
      icon: faTrowelBricks,
      description: "Expert masonry, stonework, and brickwork for structural and decorative purposes."
    },
   
  ];

  const toggleVideo1 = () => {
    if (video1Ref.current) {
      if (isVideo1Playing) {
        video1Ref.current.pause();
      } else {
        video1Ref.current.play();
      }
      setIsVideo1Playing(!isVideo1Playing);
    }
  };

  const toggleVideo2 = () => {
    if (video2Ref.current) {
      if (isVideo2Playing) {
        video2Ref.current.pause();
      } else {
        video2Ref.current.play();
      }
      setIsVideo2Playing(!isVideo2Playing);
    }
  };

  const toggleMute1 = () => {
    if (video1Ref.current) {
      video1Ref.current.muted = !video1Ref.current.muted;
      setIsVideo1Muted(!isVideo1Muted);
    }
  };

  const toggleMute2 = () => {
    if (video2Ref.current) {
      video2Ref.current.muted = !video2Ref.current.muted;
      setIsVideo2Muted(!isVideo2Muted);
    }
  };

  const openVideoPopup = (videoSrc, title) => {
    setSelectedVideo({ src: videoSrc, title });
  };

  const closeVideoPopup = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="relative overflow-hidden">
      {/* Video Popup Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4">
          <div className="relative w-full max-w-4xl mx-auto">
            <button
              onClick={closeVideoPopup}
              className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors z-10"
            >
              <FontAwesomeIcon icon={faTimes} className="w-8 h-8" />
            </button>
            <div className="relative aspect-video rounded-xl overflow-hidden">
              <video
                className="w-full h-full object-contain"
                controls
                autoPlay
                src={selectedVideo.src}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-white">{selectedVideo.title}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative h-[400px] md:h-[500px] bg-gradient-to-r from-yellow-900 to-gray-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        
        <div className="relative z-10 h-full flex flex-col header justify-center items-center px-4 text-center">
          <div className="mb-6">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-4 tracking-tight">
              Our Construction Services
            </h1>
            <div className="w-24 h-1 bg-yellow-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto font-light">
              Building excellence from foundation to finish
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative -mt-12 md:-mt-16 z-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-4 md:p-6 text-center shadow-xl border border-yellow-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-yellow-600 mb-3 flex justify-center">
                  <FontAwesomeIcon icon={stat.icon} className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <div className="text-lg md:text-2xl lg:text-4xl font-bold text-gray-800 mb-2">{stat.value}</div>
                <div className="text-xs md:text-sm lg:text-base text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left Column - Services List */}
          <div className="px-2 sm:px-0">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-6 md:mb-8">
              <span className="text-yellow-600">Our Comprehensive Services</span>
            </h2>
            
            <div className="space-y-3 sm:space-y-4">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl transition-all duration-300 group cursor-pointer"
                >
                  <div className="bg-gray-100 p-2 sm:p-3 rounded-lg group-hover:bg-gray-200 transition-colors flex-shrink-0">
                    <FontAwesomeIcon 
                      icon={service.icon} 
                      className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 mb-1">
                      {service.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="border rounded-2xl text-white bg-gradient-to-r from-yellow-600 to-yellow-700 h-56 mt-8 text-center p-10">
              <h2 className="text-2xl font-bold">Ready to Build Your Vision</h2>
              <Link to='/contact'>
              <button className='bg-white text-yellow-700 py-3 px-4 rounded-xl font-bold mt-6'>Get a Quote</button>
              </Link>
            </div>
          </div>

          {/* Right Column - Image Gallery */}
          <div className="px-2 sm:px-0">
            {/* First Row - Two Images */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                <img 
                  src={img3} 
                  alt="New building construction" 
                  className="w-full h-40 sm:h-48 md:h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                  New Builds
                </div>
              </div>
              <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                <img 
                  src={img4} 
                  alt="Building extension" 
                  className="w-full h-40 sm:h-48 md:h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                  Extensions
                </div>
              </div>
            </div>

            {/* Second Row - Gallery Grid */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-3 sm:space-y-4">
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                  <img 
                    src={img1} 
                    alt="Kitchen renovation" 
                    className="w-full h-32 sm:h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                    Kitchens
                  </div>
                </div>
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                  <img 
                    src={img7} 
                    alt="Stonework" 
                    className="w-full h-32 sm:h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                    Stonework
                  </div>
                </div>
              </div>
              <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                <img 
                  src={img5} 
                  alt="Commercial construction" 
                  className="w-full h-full min-h-[200px] sm:min-h-[300px] md:min-h-[400px] object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                  Commercial
                </div>
              </div>
            </div>

            {/* Third Row */}
            <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-5">
              <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                <img 
                  src={img6} 
                  alt="Commercial construction" 
                  className="w-full h-40 sm:h-48 md:h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                  Commercial
                </div>
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                  <img 
                    src={img2} 
                    alt="Kitchen renovation" 
                    className="w-full h-32 sm:h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                    Kitchens
                  </div>
                </div>
                <div className="relative group overflow-hidden rounded-xl sm:rounded-2xl">
                  <img 
                    src={img7} 
                    alt="Stonework" 
                    className="w-full h-32 sm:h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white font-semibold text-sm sm:text-base opacity-0 group-hover:opacity-100 transition-opacity">
                    Stonework
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Showcase Section */}
        <div className="mt-16 md:mt-32 text-center px-2 sm:px-0">
          <div className="mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              <span className="text-yellow-600">Our Work in Progress</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              See how we transform visions into reality with precision and craftsmanship
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
            {/* Video 1 */}
            <div 
              onClick={() => openVideoPopup(video1, "Construction Process")}
              className="relative group overflow-hidden rounded-xl md:rounded-2xl lg:rounded-3xl shadow-lg md:shadow-xl cursor-pointer"
            >
              <div className="relative aspect-video">
                <video
                  ref={video1Ref}
                  className="w-full h-full object-cover"
                  loop
                  muted={isVideo1Muted}
                  playsInline
                  onClick={(e) => e.stopPropagation()}
                >
                  <source src={video1} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Video Controls Overlay */}
                <div 
                  className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3 sm:gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVideo1();
                      }}
                      className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isVideo1Playing ? (
                        <FontAwesomeIcon icon={faPause} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      ) : (
                        <FontAwesomeIcon icon={faPlay} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMute1();
                      }}
                      className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isVideo1Muted ? (
                        <FontAwesomeIcon icon={faVolumeMute} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      ) : (
                        <FontAwesomeIcon icon={faVolumeUp} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Play Button (when not playing) */}
                {!isVideo1Playing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVideo1();
                    }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="bg-white/20 backdrop-blur-sm p-4 sm:p-5 lg:p-6 rounded-full hover:bg-white/30 transition-all duration-300 group-hover:scale-110">
                      <FontAwesomeIcon icon={faPlay} className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                    </div>
                  </button>
                )}
              </div>
              
              <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                <span className="bg-yellow-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                  Construction Process
                </span>
              </div>
              
              {/* Click to expand hint */}
              <div className="absolute top-3 sm:top-4 right-3 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                  Click to expand
                </span>
              </div>
            </div>

            {/* Video 2 */}
            <div 
              onClick={() => openVideoPopup(video2, "Project Highlights")}
              className="relative group overflow-hidden rounded-xl md:rounded-2xl lg:rounded-3xl shadow-lg md:shadow-xl cursor-pointer"
            >
              <div className="relative aspect-video">
                <video
                  ref={video2Ref}
                  className="w-full h-full object-cover"
                  loop
                  muted={isVideo2Muted}
                  playsInline
                  onClick={(e) => e.stopPropagation()}
                >
                  <source src={video2} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* Video Controls Overlay */}
                <div 
                  className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3 sm:gap-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVideo2();
                      }}
                      className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isVideo2Playing ? (
                        <FontAwesomeIcon icon={faPause} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      ) : (
                        <FontAwesomeIcon icon={faPlay} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      )}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMute2();
                      }}
                      className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-full hover:bg-white/30 transition-colors"
                    >
                      {isVideo2Muted ? (
                        <FontAwesomeIcon icon={faVolumeMute} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      ) : (
                        <FontAwesomeIcon icon={faVolumeUp} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-white" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Play Button (when not playing) */}
                {!isVideo2Playing && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleVideo2();
                    }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="bg-white/20 backdrop-blur-sm p-4 sm:p-5 lg:p-6 rounded-full hover:bg-white/30 transition-all duration-300 group-hover:scale-110">
                      <FontAwesomeIcon icon={faPlay} className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                    </div>
                  </button>
                )}
              </div>
              
              <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                <span className="bg-yellow-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm font-semibold">
                  Project Highlights
                </span>
              </div>
              
              {/* Click to expand hint */}
              <div className="absolute top-3 sm:top-4 right-3 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                  Click to expand
                </span>
              </div>
            </div>
          </div>

         
        </div>
      </div>
    </div>
  );
};

export default Services;